/**
 * PerChildComponentsReview · iter303ak-fix24 — Accepted · T30 · Action 10
 * ──────────────────────────────────────────────────────────────────────
 * Per-child accordion for reviewing auto-pushed component_lines. One row
 * per accepted child (Trevor's Maintenance + Repairs split scenario).
 * Each row:
 *   • fetches /appraisals/{child_id}/components-summary (auto-pushed_at,
 *     line count, by_type breakdown, auto_push_result)
 *   • "Open Components tab" popup → /appraisals/{child_id}?embed=components
 *   • "Reviewed & ready" checkbox
 * Continue disabled until all rows ticked.
 *
 * Auto-push fires from workflow_runs.py when T10 A20/A30 completes, so
 * by the time this action mounts the data is usually already present.
 * Extra safety net: on mount we call /auto-push-children-components once
 * (idempotent) to catch any missed trigger.
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { CheckCircle2, ExternalLink, Package, AlertTriangle, RefreshCw } from 'lucide-react';
import QuoteCostCheck from '../components/QuoteCostCheck';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function PerChildComponentsReview({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};
  const appraisalId = capturedData?.appraisal_id;

  const acceptedChildren = useMemo(() => {
    const decisions = capturedData?.decisions
      || capturedData?.accepted_variants_pick?.decisions
      || capturedData?.await_quote_response?.decisions
      || capturedData?.appraisal?.child_decisions
      || capturedData?.appraisal_record?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [capturedData]);

  const [summaries, setSummaries] = useState({}); // child_id -> summary
  const [reviewedIds, setReviewedIds] = useState(() => new Set(value?.reviewed_ids || []));
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [autoPushBusy, setAutoPushBusy] = useState(false);
  const autoPushedRef = useRef(false);
  const [openWindow, setOpenWindow] = useState({}); // child_id -> window ref

  const loadSummaries = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true);
    try {
      const results = {};
      for (const c of acceptedChildren) {
        try {
          const { data } = await api.get(`/appraisals/${c.child_id}/components-summary`);
          results[c.child_id] = data;
        } catch (e) {
          results[c.child_id] = { error: e?.response?.data?.detail || 'Failed to load summary' };
        }
      }
      setSummaries(results);
    } finally { setLoading(false); }
  }, [acceptedChildren]);

  // On mount: trigger auto-push (idempotent safety net) + load summaries.
  useEffect(() => {
    if (!appraisalId || autoPushedRef.current) return;
    autoPushedRef.current = true;
    (async () => {
      setAutoPushBusy(true);
      try {
        await api.post(`/appraisals/${appraisalId}/auto-push-children-components`);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Auto-push failed — you can still review manually.');
      } finally {
        setAutoPushBusy(false);
      }
      await loadSummaries();
    })();
  }, [appraisalId, loadSummaries]);

  // Poll for popup close → refresh that child's summary.
  useEffect(() => {
    const timers = [];
    for (const [cid, win] of Object.entries(openWindow)) {
      if (!win) continue;
      const timer = setInterval(async () => {
        if (win.closed) {
          clearInterval(timer);
          setOpenWindow(prev => ({ ...prev, [cid]: null }));
          try {
            const { data } = await api.get(`/appraisals/${cid}/components-summary`);
            setSummaries(prev => ({ ...prev, [cid]: data }));
          } catch { /* silent */ }
        }
      }, 500);
      timers.push(timer);
    }
    return () => { timers.forEach(clearInterval); };
  }, [openWindow]);

  const openChildComponentsTab = (child_id) => {
    const url = `/appraisals/${child_id}?embed=components`;
    const win = window.open(url, `child_components_${child_id}`, 'width=1400,height=900');
    if (!win) { alert('Popup blocked — allow popups for this site and try again.'); return; }
    setOpenWindow(prev => ({ ...prev, [child_id]: win }));
  };

  const [costOk, setCostOk] = useState({});
  const setCostOkFor = useCallback((k, ok) => setCostOk(prev => (prev[k] === ok ? prev : { ...prev, [k]: ok })), []);
  const toggleReviewed = (child_id) => {
    setReviewedIds(prev => {
      const next = new Set(prev);
      if (next.has(child_id)) next.delete(child_id); else next.add(child_id);
      return next;
    });
  };

  // Push to captured_data on change.
  useEffect(() => {
    if (onChange) {
      onChange({
        reviewed_ids: Array.from(reviewedIds),
        summaries_snapshot: summaries,
      });
    }
  }, [reviewedIds, summaries, onChange]);

  const allReviewed = acceptedChildren.length > 0 && acceptedChildren.every(c => reviewedIds.has(c.child_id));

  const advance = () => {
    if (!allReviewed) return;
    onNext();
  };

  const rePushAll = async () => {
    if (!appraisalId) return;
    setAutoPushBusy(true);
    try {
      await api.post(`/appraisals/${appraisalId}/auto-push-children-components`);
      await loadSummaries();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Re-push failed');
    } finally {
      setAutoPushBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="wf-per-child-components-review">
      <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
        <div className="flex items-start gap-3">
          <Package className="w-5 h-5 mt-0.5 shrink-0 text-emerald-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-emerald-900">Review Component Data — per accepted quote</div>
            <div className="text-sm mt-1 text-emerald-800">
              {params.prompt || 'Component data is auto-pulled from each quote\'s builder. Open the Components tab for any quote that needs correction, then tick each row as ready.'}
            </div>
          </div>
          <button
            type="button"
            onClick={rePushAll}
            disabled={autoPushBusy}
            className="px-3 py-1.5 rounded-md text-xs font-semibold text-emerald-800 bg-white border border-emerald-300 hover:bg-emerald-50 disabled:opacity-40 inline-flex items-center gap-1.5 shrink-0"
            title="Re-run the builder → component sync across all accepted quotes"
            data-testid="wf-per-child-components-repush-all"
          >
            <RefreshCw size={12} className={autoPushBusy ? 'animate-spin' : ''} />
            {autoPushBusy ? 'Syncing…' : 'Re-sync all'}
          </button>
        </div>
      </div>

      {loading && <div className="p-6 text-sm text-slate-400 italic">Loading component summaries…</div>}

      {!loading && acceptedChildren.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No accepted quotes found. Go back and confirm at least one before proceeding.
        </div>
      )}

      {!loading && acceptedChildren.length > 0 && (
        <div className="space-y-2">
          {acceptedChildren.map((c) => {
            const s = summaries[c.child_id] || {};
            const reviewed = reviewedIds.has(c.child_id);
            const label = [s.quote_type_ref || c.quote_type_ref, s.variant_label || c.variant_label].filter(Boolean).join(' · ');
            const byType = s.by_type || {};
            const byTypeEntries = Object.entries(byType);
            const bySource = s.by_source || {};
            const autoPushed = s.auto_pushed_at;
            const autoResult = s.auto_push_result;
            const isSkipped = autoResult?.skipped;
            return (
              <div key={c.child_id} className={`p-3 border rounded-lg ${reviewed ? 'bg-emerald-50/40 border-emerald-300' : 'bg-white border-slate-200'}`} data-testid={`wf-per-child-components-row-${c.child_id}`}>
                <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-medium text-slate-800">{label || 'Quote'}</div>
                    {s.appraisal_no && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">
                        A{s.appraisal_no}
                      </span>
                    )}
                    <span className="text-[10px] font-semibold text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                      {s.total || 0} line{(s.total || 0) === 1 ? '' : 's'}
                    </span>
                    {isSkipped && (
                      <span className="text-[10px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-flex items-center gap-1">
                        <AlertTriangle size={10} /> Auto-push skipped
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => openChildComponentsTab(c.child_id)}
                    className="px-3 py-1.5 rounded-md text-xs font-semibold text-emerald-700 bg-white border border-emerald-300 hover:bg-emerald-50 inline-flex items-center gap-1.5"
                    data-testid={`wf-per-child-components-open-${c.child_id}`}
                  >
                    <ExternalLink size={12} /> Open Components tab
                  </button>
                </div>

                <div className="text-[11px] text-slate-600 mb-2">
                  {autoPushed ? (
                    <>Auto-pulled: <b>{new Date(autoPushed).toLocaleString()}</b>{autoResult?.source && <> · Source: <b>{autoResult.source}</b></>}</>
                  ) : (
                    <span className="text-amber-800">No auto-pull yet — you may need to click &quot;Re-sync all&quot; or use the Components tab manually.</span>
                  )}
                  {isSkipped && autoResult?.error && (
                    <div className="mt-1 text-amber-800 text-[10.5px]">Reason: {autoResult.error}</div>
                  )}
                </div>

                {byTypeEntries.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {byTypeEntries.map(([type, count]) => (
                      <span key={type} className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700">
                        {type}: <b>{count}</b>
                      </span>
                    ))}
                  </div>
                )}
                {Object.keys(bySource).length > 0 && (
                  <div className="mb-2 text-[10px] text-slate-500">
                    Sources: {Object.entries(bySource).map(([src, n]) => `${src} (${n})`).join(' · ')}
                  </div>
                )}

                <div className="mb-2">
                  <QuoteCostCheck api={api} childId={c.child_id} rowKey={c.child_id} refreshKey={autoPushed || ''} onOkChange={setCostOkFor} />
                </div>
                <label className="flex items-center gap-2 pt-2 border-t border-slate-100 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={reviewed}
                    disabled={!reviewed && !costOk[c.child_id]}
                    onChange={() => toggleReviewed(c.child_id)}
                    className="rounded"
                    data-testid={`wf-per-child-components-reviewed-${c.child_id}`}
                  />
                  <span className={`text-xs font-medium ${reviewed ? 'text-emerald-800' : 'text-slate-600'}`}>
                    Reviewed &amp; ready
                  </span>
                </label>
              </div>
            );
          })}
        </div>
      )}

      {err && (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded p-2">{err}</div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? (
          <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-per-child-components-back">← Back</button>
        ) : <span />}
        <div className="flex gap-2">
          {onCancel && (
            <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="wf-per-child-components-cancel">Cancel</button>
          )}
          <button
            onClick={advance}
            disabled={!allReviewed}
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2"
            data-testid="wf-per-child-components-continue"
            title={allReviewed ? 'All quotes reviewed — continue to Contractors review' : 'Tick each quote as reviewed to continue'}
          >
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
